import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import * as api from '../../lib/api'
import Modal from '../../components/Modal'
import AutoSalaryBreakdown from '../../components/AutoSalaryBreakdown'
import DateRangeFilter from '../../components/DateRangeFilter'

// Import split subcomponents
import PaymentStats from './components/PaymentStats'
import PaymentFormModal from './components/PaymentFormModal'
import PaidPeriodModal from './components/PaidPeriodModal'
import DisputeModal from './components/DisputeModal'
import PaymentsFilters from './components/PaymentsFilters'
import AllSalariesSummary from './components/AllSalariesSummary'
import PaymentsList from './components/PaymentsList'
import PaySalaryModal from './components/PaySalaryModal'
import { SalaryDetailModal } from './components/SalaryDetailModal'
import { ExportModal } from './components/ExportModal'
import { PayoutsDetailModal } from './components/PayoutsDetailModal'

import './PaymentsPage.css'

import {
  formatDate,
  formatDateTime,
  isImageUrl,
  renderEvidence,
  formatMoney,
  typeConfig,
  today,
  firstDayOfMonth,
  lastDayOfMonth,
  getNearestOwedPeriod
} from './utils/paymentHelpers'

const mapSalaryObject = (salary, options = {}) => {
  const isAnalytics = Boolean(options.isAnalytics || salary.isAnalytics)
  const isReview = !isAnalytics && Boolean(salary.isReviewRequired || salary.hasUnresolvedLegacy)
  const debt = isAnalytics ? null : (isReview ? 0 : Math.round(salary.periodDebt ?? salary.total))
  return {
    ...salary,
    isAnalytics,
    isAnalyticsRange: isAnalytics,
    isSettlementPeriod: !isAnalytics,
    isReviewRequired: isReview,
    status: isReview ? 'unresolved' : (isAnalytics ? 'analytics' : salary.status),
    unresolvedDebt: isReview ? (salary.unresolvedDebt ?? Math.round(salary.total)) : 0,
    actualDebt: debt,
    upcomingDebt: isAnalytics ? 0 : debt,
    actualSalary: salary.total,
    actualRateDebt: isReview || isAnalytics ? 0 : (salary.rateDebt ?? 0),
    actualBonusesDebt: isReview || isAnalytics ? 0 : (salary.bonusesDebt ?? 0),
    actualBonusesPayable: salary.bonusesPayable ?? true,
    actualBonusPayoutDateStr: salary.bonusPayoutDateStr ?? '',
    actualSplitEnabled: salary.splitEnabled ?? false,
    actualRateEarned: salary.rateEarned ?? 0,
    actualBonusesEarned: salary.bonusesEarned ?? 0,
    actualRatePaid: salary.ratePaid ?? 0,
    actualBonusesPaid: salary.bonusesPaid ?? 0,
    periodPaid: salary.paid || 0,
    periodDebt: debt,
    periodRateDebt: isReview || isAnalytics ? 0 : (salary.rateDebt ?? 0),
    periodBonusesDebt: isReview || isAnalytics ? 0 : (salary.bonusesDebt ?? 0),
    physicalPaidInPeriod: salary.paid || 0,
    paymentStatus: salary.paymentStatus || (debt != null && debt <= 0 ? 'paid' : 'unpaid'),
    paymentStatusReason: salary.paymentStatusReason || null,
    paymentPaidAmount: salary.paymentPaidAmount !== undefined ? salary.paymentPaidAmount : (salary.paid || 0),
    paymentOutstandingAmount: salary.paymentOutstandingAmount !== undefined ? salary.paymentOutstandingAmount : (debt || 0),
    paymentAmountsExact: salary.paymentAmountsExact !== undefined ? salary.paymentAmountsExact : true,
  }
}

export default function PaymentsPage({ isEmbedded = false }) {
  const { profile, isAdmin, isOwner, activePvz, selectedPvzIds, adminPermissions: userPermissions } = useAuth()
  const canManagePayments = isAdmin || userPermissions?.canManagePayments === true

  const toast = useToast()
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filterType, setFilterType] = useState('')
  const [filterEmployee, setFilterEmployee] = useState('')

  const getDefaultPeriod = (payoutFrequency = 'bi_monthly', cutoverDate = null) => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() // 0-11
    const pad = (n) => String(n).padStart(2, '0')
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

    if (payoutFrequency === 'weekly') {
      // Текущая неделя (Пн - Вс)
      const day = now.getDay()
      const diffToMonday = day === 0 ? -6 : 1 - day
      const monday = new Date(now)
      monday.setDate(now.getDate() + diffToMonday)
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      const base = { start: fmt(monday), end: fmt(sunday) }
      if (cutoverDate && cutoverDate > base.start && cutoverDate <= base.end) {
        base.start = cutoverDate
      }
      return base
    } else if (payoutFrequency === 'monthly') {
      // Весь текущий месяц
      const lastDay = new Date(year, month + 1, 0).getDate()
      let start = `${year}-${pad(month + 1)}-01`
      const end = `${year}-${pad(month + 1)}-${pad(lastDay)}`
      if (cutoverDate && cutoverDate > start && cutoverDate <= end) {
        start = cutoverDate
      }
      return { start, end }
    } else {
      // Полумесячные выплаты (1-15 или 16-конец)
      const date = now.getDate()
      const lastDay = new Date(year, month + 1, 0).getDate()
      if (date <= 15) {
        let start = `${year}-${pad(month + 1)}-01`
        const end = `${year}-${pad(month + 1)}-15`
        if (cutoverDate && cutoverDate > start && cutoverDate <= end) {
          start = cutoverDate
        }
        return { start, end }
      } else {
        let start = `${year}-${pad(month + 1)}-16`
        const end = `${year}-${pad(month + 1)}-${pad(lastDay)}`
        if (cutoverDate && cutoverDate > start && cutoverDate <= end) {
          start = cutoverDate
        }
        return { start, end }
      }
    }
  }
  const defaultPeriod = getDefaultPeriod('bi_monthly')
  const [filterPeriodStart, setFilterPeriodStart] = useState(defaultPeriod.start)
  const [filterPeriodEnd, setFilterPeriodEnd] = useState(defaultPeriod.end)

  // Спор (апелляция)
  const [disputeId, setDisputeId] = useState(null)
  const [disputeComment, setDisputeComment] = useState('')
  const [disputing, setDisputing] = useState(false)
  const [disputeFile, setDisputeFile] = useState(null)
  const [disputePreview, setDisputePreview] = useState(null)
  const [disputeVideoLink, setDisputeVideoLink] = useState('')

  const [employees, setEmployees] = useState([])
  const [autoSalary, setAutoSalary] = useState(null)
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null)
  const [showUpcomingPayouts, setShowUpcomingPayouts] = useState(false)

  // Для отображения ЗП всех сотрудников у админа
  const [allSalaries, setAllSalaries] = useState([])
  const [loadingAllSalaries, setLoadingAllSalaries] = useState(true)
  const [extendedSalaries, setExtendedSalaries] = useState([])

  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [commentModal, setCommentModal] = useState(null)
  const [imagePreviewModal, setImagePreviewModal] = useState(null)
  const [salaryDetailModal, setSalaryDetailModal] = useState(null)
  const [wbDeductions, setWbDeductions] = useState([])
  const [manualHours, setManualHours] = useState([])
  const [salaryConfig, setSalaryConfig] = useState(null)
  const [salaryConfigLoadState, setSalaryConfigLoadState] = useState('idle') // 'idle' | 'loading' | 'ready' | 'error'
  const [employeesLoadState, setEmployeesLoadState] = useState('idle') // 'idle' | 'loading' | 'ready' | 'error'
  const bootstrapGenRef = useRef(0)
  const loadAllGenRef = useRef(0)
  const [statDetailModal, setStatDetailModal] = useState(null)

  const [payModalData, setPayModalData] = useState(null)
  const [payAmount, setPayAmount] = useState('')
  const [payReasonCode, setPayReasonCode] = useState('all')
  const [payCustomAmount, setPayCustomAmount] = useState(false)
  const [payingSalary, setPayingSalary] = useState(false)

  // Новые стейты для гибкой выплаты по датам и чекбоксам
  const [customPeriodStart, setCustomPeriodStart] = useState('')
  const [customPeriodEnd, setCustomPeriodEnd] = useState('')
  const [includeRate, setIncludeRate] = useState(true)
  const [includeBonuses, setIncludeBonuses] = useState(true)
  const [includeAdjustments, setIncludeAdjustments] = useState(true)
  const [isManualAmount, setIsManualAmount] = useState(false)
  const [calculatedData, setCalculatedData] = useState(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [payComment, setPayComment] = useState('')
  const [isCommentManuallyEdited, setIsCommentManuallyEdited] = useState(false)
  const [employeeShifts, setEmployeeShifts] = useState(null)
  const [showPayModalDetails, setShowPayModalDetails] = useState(false)
  const isInitialModalCalc = React.useRef(false) // Флаг первого расчёта после открытия модалки

  const [showPayoutsDetailModal, setShowPayoutsDetailModal] = useState(false)

  // Модальное окно «Период уже оплачен»
  const [paidPeriodModal, setPaidPeriodModal] = useState(null)
  const [paidPeriodSaving, setPaidPeriodSaving] = useState(false)

  // Загрузка рабочих смен выбранного сотрудника для отображения на календаре при выплате ЗП
  useEffect(() => {
    if (payModalData?.employeeId) {
      api.getEmployeeShiftsMarkers(payModalData.employeeId, payModalData.employeeName)
        .then(markers => {
          setEmployeeShifts(markers)
        })
        .catch(err => {
          console.error('Ошибка загрузки смен сотрудника для календаря:', err)
          setEmployeeShifts(null)
        })
    } else {
      setEmployeeShifts(null)
    }
  }, [payModalData?.employeeId])

  // Расчет на лету при смене дат в модалке выплаты
  useEffect(() => {
    if (!payModalData?.employeeId || !customPeriodStart || !customPeriodEnd) {
      setCalculatedData(null)
      return
    }

    let isCurrent = true
    setIsCalculating(true)

    api.calculateAutoSalary(payModalData.employeeId, customPeriodStart, customPeriodEnd, { mode: 'settlement' })
      .then(res => {
        if (!isCurrent) return
        setCalculatedData(res)
        setIsCalculating(false)
      })
      .catch(err => {
        console.error('Ошибка на лету расчета ЗП в модалке:', err)
        if (isCurrent) setIsCalculating(false)
      })

    return () => {
      isCurrent = false
    }
  }, [payModalData?.employeeId, customPeriodStart, customPeriodEnd])

  // Пересчет суммы выплаты на основе чекбоксов и полученного расчета
  useEffect(() => {
    if (isManualAmount) return

    // При первом открытии модалки пропускаем пересчёт — используем предрассчитанный долг
    if (isInitialModalCalc.current) {
      isInitialModalCalc.current = false
      return
    }

    // Защита: если бонусы заблокированы (раздельная выплата), не включаем их в сумму
    const canPayBonuses = (calculatedData?.bonusesPayable !== undefined ? calculatedData.bonusesPayable : payModalData?.bonusesPayable) !== false

    const defaultStart = payModalData?.naturalPeriod?.start || payModalData?.period?.start
    const defaultEnd = payModalData?.naturalPeriod?.end || payModalData?.period?.end
    const isDefaultPeriod = !payModalData?.isFromAnalytics && customPeriodStart === defaultStart && customPeriodEnd === defaultEnd

    if (isDefaultPeriod && payModalData) {
      let sum = 0
      if (includeRate) {
        sum += (payModalData.rateDebt || 0)
      }
      if (includeBonuses && canPayBonuses) {
        sum += (payModalData.bonusesDebt || 0)
      }
      setPayAmount(String(Math.max(0, Math.round(sum))))
    } else {
      if (!calculatedData) {
        setPayAmount('')
        return
      }

      let sum = 0
      if (includeRate) {
        sum += (calculatedData.rateDebt || 0)
      }
      if (includeBonuses && canPayBonuses) {
        sum += (calculatedData.bonusesDebt || 0)
      }
      setPayAmount(String(Math.max(0, Math.round(sum))))
    }
  }, [calculatedData, includeRate, includeBonuses, isManualAmount, customPeriodStart, customPeriodEnd, payModalData])

  // Автогенерация комментария к выплате
  useEffect(() => {
    if (isCommentManuallyEdited) return
    const parts = []
    if (includeRate) parts.push('Оклад')
    if (includeBonuses) parts.push('Бонусы')
    if (includeAdjustments) parts.push('Вычеты/Премии')

    const startStr = customPeriodStart ? formatDate(customPeriodStart) : ''
    const endStr = customPeriodEnd ? formatDate(customPeriodEnd) : ''

    let desc = `Выплата ЗП`
    if (startStr && endStr) {
      desc += ` за ${startStr}—${endStr}`
    }
    if (parts.length > 0) {
      desc += ` (${parts.join(' + ')})`
    }
    setPayComment(desc)
  }, [customPeriodStart, customPeriodEnd, includeRate, includeBonuses, includeAdjustments, isCommentManuallyEdited])

  const handleOpenPayModal = (employeeSalary) => {
    // DEFENSIVE GUARD: Never open pay modal for an employee whose salary is already paid
    if (employeeSalary?.paymentStatus === 'paid') {
      return
    }

    const isFromAnalytics = Boolean(employeeSalary.isAnalytics || employeeSalary.isAnalyticsRange)
    const isReview = !isFromAnalytics && Boolean(employeeSalary.isReviewRequired || employeeSalary.hasUnresolvedLegacy)
    const modalEmp = employees.find(e => e.id === employeeSalary.employeeId) || employeeSalary

    // SEPARATE VIEW RANGE FROM PAYOUT PERIOD:
    // По умолчанию модалка выплаты открывается на естественном расчётном периоде сотрудника,
    // а не на произвольном фильтре просмотра таблицы (view filter).
    const naturalPeriod = api.getEmployeeCurrentSalaryPeriod
      ? api.getEmployeeCurrentSalaryPeriod(modalEmp, salaryConfig)
      : null

    const start = naturalPeriod?.start || (!isFromAnalytics && employeeSalary.actualPeriod?.start) || filterPeriodStart || firstDayOfMonth()
    const end = naturalPeriod?.end || (!isFromAnalytics && employeeSalary.actualPeriod?.end) || filterPeriodEnd || lastDayOfMonth()

    const debt = isFromAnalytics ? 0 : (isReview ? 0 : (employeeSalary.actualDebt !== undefined ? employeeSalary.actualDebt : employeeSalary.total))

    setPayModalData({
      employeeId: employeeSalary.employeeId,
      employeeName: employeeSalary.employeeName,
      employment_type: modalEmp?.employment_type || employeeSalary.employment_type,
      payout_frequency_override: modalEmp?.payout_frequency_override || employeeSalary.payout_frequency_override,
      hired_at: modalEmp?.hired_at || employeeSalary.hired_at,
      pay_day: modalEmp?.pay_day ?? employeeSalary.pay_day,
      period: isFromAnalytics ? naturalPeriod : employeeSalary.actualPeriod,
      naturalPeriod: naturalPeriod,
      totalEarned: isFromAnalytics ? 0 : employeeSalary.total,
      debt: debt,
      rateDebt: isFromAnalytics ? 0 : (isReview ? 0 : (employeeSalary.actualRateDebt ?? 0)),
      bonusesDebt: isFromAnalytics ? 0 : (isReview ? 0 : (employeeSalary.actualBonusesDebt ?? 0)),
      bonusesPayable: isFromAnalytics ? true : (employeeSalary.actualBonusesPayable ?? true),
      bonusPayoutDateStr: isFromAnalytics ? '' : (employeeSalary.actualBonusPayoutDateStr ?? ''),
      splitEnabled: isFromAnalytics ? false : (employeeSalary.actualSplitEnabled ?? false),
      isReviewRequired: isReview,
      hasUnresolvedLegacy: isReview,
      unresolvedDebt: isFromAnalytics ? 0 : (isReview ? (employeeSalary.unresolvedDebt ?? Math.round(employeeSalary.total)) : 0),
      isFromAnalytics: isFromAnalytics,
      isCompleteChunk: employeeSalary.isCompleteChunk,
      shiftsCountInChunk: employeeSalary.shiftsCountInChunk,
      requiredShiftsCount: employeeSalary.requiredShiftsCount,
      hasOpenShift: Boolean(employeeSalary.hasOpenShift || employeeSalary.isOpenShift),
      isOpenShift: Boolean(employeeSalary.hasOpenShift || employeeSalary.isOpenShift),
      isPayable: employeeSalary.isPayable
    })

    setCustomPeriodStart(start)
    setCustomPeriodEnd(end)
    setIncludeRate(!isReview)
    // Если бонусы заблокированы (раздельная выплата), не включаем их по умолчанию
    const isBonusesAvailable = isFromAnalytics ? true : (employeeSalary.actualBonusesPayable ?? true)
    setIncludeBonuses(!isReview && isBonusesAvailable)
    setIncludeAdjustments(!isReview)
    setIsManualAmount(false)
    // При открытии из аналитики обязательно требуется динамический расчёт для натурального settlement периода
    isInitialModalCalc.current = !isReview && !isFromAnalytics
    setCalculatedData(null)
    setPayAmount(isFromAnalytics ? '' : (isReview ? '' : String(Math.max(0, Math.round(debt || 0)))))
    setPayReasonCode('all')
    setPayCustomAmount(false)
    setPayComment('')
    setIsCommentManuallyEdited(false)
    setShowPayModalDetails(false)
  }

  const [formData, setFormData] = useState({
    employee_id: '',
    type: 'bonus',
    amount: '',
    period_start: today(),
    period_end: today(),
    comment: '',
    reason_code: '',
    file: null,
  })

  // Экспорт
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportStartDate, setExportStartDate] = useState(today())
  const [exportEndDate, setExportEndDate] = useState(today())
  const [exportSelectedEmployees, setExportSelectedEmployees] = useState([])
  const [exporting, setExporting] = useState(false)

  const handleExportSalaries = async () => {
    setExporting(true)
    try {
      const results = []
      for (const empId of exportSelectedEmployees) {
        const emp = employees.find(e => e.id === empId)
        const salary = await api.calculateAutoSalary(empId, exportStartDate, exportEndDate, { mode: 'analytics' })
        results.push({
          'Сотрудник': emp?.name || 'Неизвестно',
          'Дней': salary.daysWorked,
          'Часов': salary.totalHours,
          'Приёмка (вовремя)': salary.totalAccepted,
          'Приёмка (опоздание)': salary.totalAcceptedLate,
          'Выдача': salary.totalIssued,
          'Возвраты': salary.totalReturned || 0,
          'Премии': salary.totalBonuses,
          'Штрафы': salary.totalFines,
          'Вычеты WB': salary.totalWbDeductions,
          'ИТОГО': salary.total
        })
      }

      if (results.length === 0) {
        toast.error('Нет данных для экспорта')
        return
      }

      const headers = Object.keys(results[0])
      const tsvContent = [
        headers.join('\t'),
        ...results.map(row => headers.map(h => String(row[h] || 0)).join('\t'))
      ].join('\n')

      const blob = new Blob(['\ufeff' + tsvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `salary_report_${exportStartDate}_${exportEndDate}.xls`)
      link.click()
      setShowExportModal(false)
      toast.success('Отчет сформирован')
    } catch (err) {
      console.error(err)
      toast.error('Ошибка при формировании отчета')
    } finally {
      setExporting(false)
    }
  }

  // Единый авторитетный bootstrap-эффект: сброс контекста + генерационная защита + параллельная загрузка
  useEffect(() => {
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    if (!isAdmin || !pvzFilter) {
      ++bootstrapGenRef.current
      ++loadAllGenRef.current
      setEmployees([])
      setSalaryConfig(null)
      setAllSalaries([])
      setExtendedSalaries([])
      setPayments([])
      setWbDeductions([])
      setManualHours([])
      setSalaryConfigLoadState('idle')
      setEmployeesLoadState('idle')
      setLoading(false)
      setLoadingAllSalaries(false)
      return
    }

    // 1. Инкремент поколений: текущий запуск становится единственно валидным
    const currentGen = ++bootstrapGenRef.current
    ++loadAllGenRef.current

    // 2. Очистка stale scoped state ДО отправки запросов (включая сотрудников прошлого ПВЗ)
    setEmployees([])
    setSalaryConfig(null)
    setAllSalaries([])
    setExtendedSalaries([])
    setPayments([])
    setWbDeductions([])
    setManualHours([])

    // 3. Выставление состояний загрузки
    setSalaryConfigLoadState('loading')
    setEmployeesLoadState('loading')
    setLoading(true)
    setLoadingAllSalaries(true)

    const empPromise = (api.getSalaryCandidates ? api.getSalaryCandidates(pvzFilter, profile?.org_id) : api.getEmployeesList(pvzFilter, profile?.org_id))
      .then(data => {
        if (bootstrapGenRef.current !== currentGen) return null
        setEmployees(data || [])
        setEmployeesLoadState('ready')
        return data
      })
      .catch(err => {
        if (bootstrapGenRef.current !== currentGen) return null
        console.error('Ошибка загрузки сотрудников:', err)
        setEmployees([])
        setEmployeesLoadState('error')
        return null
      })

    const cfgPromise = api.getSalaryConfig(pvzFilter)
      .then(cfg => {
        if (bootstrapGenRef.current !== currentGen) return null
        setSalaryConfig(cfg || null)
        const cutover = cfg?.settlementCutoverDate || cfg?.settlement_cutover_date || null
        const period = getDefaultPeriod(cfg?.payoutFrequency || cfg?.payout_frequency, cutover)
        setFilterPeriodStart(period.start)
        setFilterPeriodEnd(period.end)
        setSalaryConfigLoadState('ready')
        return cfg
      })
      .catch(err => {
        if (bootstrapGenRef.current !== currentGen) return null
        console.error('Ошибка загрузки конфигурации зарплат:', err)
        setSalaryConfig(null)
        setSalaryConfigLoadState('error')
        return null
      })

    Promise.all([empPromise, cfgPromise])
      .then(() => {
        if (bootstrapGenRef.current !== currentGen) return
        setLoading(false)
      })
      .catch(err => {
        if (bootstrapGenRef.current !== currentGen) return
        console.error('Bootstrap error:', err)
        setLoading(false)
        setLoadingAllSalaries(false)
      })
  }, [isAdmin, activePvz?.id, selectedPvzIds?.join(','), profile?.org_id, profile?.id])

  // Для сотрудника — автозагрузка своей ЗП при первом входе
  useEffect(() => {
    if (!isAdmin && profile?.id) {
      const start = filterPeriodStart || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
      const end = filterPeriodEnd || new Date().toISOString().split('T')[0]
      const cutover = salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null
      let isSettlement = false
      try {
        const startParts = start.split('-')
        const effectiveFreq = api.getEffectivePayoutFrequency ? api.getEffectivePayoutFrequency(profile, salaryConfig) : (salaryConfig?.payout_frequency || 'bi_monthly')
        const periods = api.getSalaryPeriods ? api.getSalaryPeriods(profile.hired_at, parseInt(startParts[0], 10), parseInt(startParts[1], 10), effectiveFreq, profile.pay_day, [], cutover) : []
        isSettlement = periods.some(p => p.start === start && p.end === end)
      } catch (e) {}
      api.calculateAutoSalary(profile.id, start, end, { mode: isSettlement ? 'settlement' : 'analytics', isAnalytics: !isSettlement })
        .then(res => {
          setAutoSalary({
            ...mapSalaryObject(res),
            employeeId: profile.id,
            employeeName: profile.name || 'Неизвестно',
            actualPeriod: { start, end, displayStart: start }
          })
        })
        .catch(console.error)
    }
  }, [isAdmin, profile?.id, filterPeriodStart, filterPeriodEnd, refreshTrigger, salaryConfig])

  // Для админа — загрузка ЗП всех сотрудников
  useEffect(() => {
    if (!isAdmin || filterEmployee) {
      setLoadingAllSalaries(false)
      return
    }

    // PHASE 1 & 2 INVARIANT:
    // UPCOMING PAYOUT CALCULATION MUST NEVER RUN WITH AN UNINITIALIZED SALARY CONFIG OR EMPLOYEES STATE
    if (salaryConfigLoadState === 'error' || employeesLoadState === 'error') {
      // Ошибка загрузки конфига или сотрудников: не рассчитываем с дефолтами/stale данными,
      // завершаем загрузку без вечного спиннера
      setAllSalaries([])
      setExtendedSalaries([])
      setLoadingAllSalaries(false)
      return
    }

    if (salaryConfigLoadState !== 'ready' || employeesLoadState !== 'ready') {
      return
    }

    if (!employees || employees.length === 0) {
      setAllSalaries([])
      setExtendedSalaries([])
      setLoadingAllSalaries(false)
      return
    }

    const currentLoadAllGen = ++loadAllGenRef.current
    setLoadingAllSalaries(true)

    const loadAll = async () => {
      try {
        const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
        const start = filterPeriodStart || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
        const end = filterPeriodEnd || new Date().toISOString().split('T')[0]

        // 1. Расчет за выбранный в фильтре период (режим auto: каждый сотрудник оценивается индивидуально)
        const teamSalaries = await api.calculateTeamSalaries(
          pvzFilter || undefined,
          start,
          end,
          employees,
          [],
          { mode: 'auto' }
        )

        // PHASE 3: STALE RESPONSE GUARD
        if (loadAllGenRef.current !== currentLoadAllGen) return

        const results = employees.map(emp => {
          try {
            const salary = teamSalaries.find(s => s.employeeId === emp.id) || {
              total: 0, daysWorked: 0, totalHours: 0, totalAccepted: 0, totalIssued: 0, basePay: 0, acceptBonus: 0, issuedBonus: 0, config: {}, dailyDetails: {},
              periodDebt: 0, rateDebt: 0, bonusesDebt: 0, paid: 0, rateEarned: 0, bonusesEarned: 0, ratePaid: 0, bonusesPaid: 0,
              bonusesPayable: true, bonusPayoutDateStr: '', splitEnabled: false,
              paymentStatus: 'unpaid', paymentStatusReason: 'no_data', paymentPaidAmount: 0, paymentOutstandingAmount: 0, paymentAmountsExact: true
            }

            const debt = Math.round(salary.periodDebt ?? salary.total)

            // Проверяем активность архивного сотрудника
            const hasActivity =
              salary.daysWorked > 0 ||
              salary.totalHours > 0 ||
              salary.total !== 0 ||
              (salary.totalBonuses || 0) > 0 ||
              (salary.totalFines || 0) > 0 ||
              (salary.totalWbDeductions || 0) > 0 ||
              debt > 0

            if (emp.is_legacy && !hasActivity) {
              return null
            }

            // Единый источник режима и периода сотрудника (из salary или через resolver)
            const modeInfo = salary.naturalPeriod
              ? {
                  isSettlementPeriod: salary.isSettlementPeriod,
                  isAnalyticsRange: salary.isAnalyticsRange,
                  period: salary.naturalPeriod
                }
              : (api.resolveEmployeeSalaryRangeMode
                  ? api.resolveEmployeeSalaryRangeMode(emp, start, end, salaryConfig)
                  : {
                      isSettlementPeriod: false,
                      isAnalyticsRange: true,
                      period: { start, end, displayStart: start }
                    })

            const period = modeInfo.period || modeInfo.naturalPeriod || { start, end, displayStart: start }
            const isMatchedSettlement = Boolean(modeInfo.isSettlementPeriod)

            return {
              ...mapSalaryObject(salary, { isAnalytics: !isMatchedSettlement }),
              employeeId: emp.id,
              employeeName: emp.name,
              employment_type: emp.employment_type,
              payout_frequency_override: emp.payout_frequency_override,
              isLegacy: emp.is_legacy,
              actualPeriod: period,
              isSettlementPeriod: isMatchedSettlement,
              isAnalyticsRange: !isMatchedSettlement,
            }
          } catch (e) {
            console.error('Ошибка расчета ЗП для сотрудника:', emp.name, e)
            return emp.is_legacy ? null : { employeeId: emp.id, employeeName: emp.name, total: 0, daysWorked: 0, totalAccepted: 0, totalIssued: 0, basePay: 0, acceptBonus: 0, issuedBonus: 0, config: {}, actualPeriod: null, actualDebt: 0, upcomingDebt: 0 }
          }
        }).filter(Boolean)

        if (loadAllGenRef.current !== currentLoadAllGen) return
        setAllSalaries(results)

        // 2. Расчет актуальных и ближайших периодов выплат для календаря «Ближайшие выплаты»
        try {
          const pvzTz = salaryConfig?.timezone || salaryConfig?.pvz_timezone || null
          const todayStr = api.getDateInTimeZone ? api.getDateInTimeZone(new Date(), pvzTz) : new Date().toISOString().split('T')[0]
          const nowParts = todayStr.split('-').map(Number)
          const nowYear = nowParts[0]
          const nowMonth = nowParts[1] // 1-12
          const cutover = salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null

          // Загружаем достоверную историю тарифов ОДИН раз для всего ПВЗ (без повторных запросов)
          let configHistory = []
          let configHistoryLoadFailed = false
          try {
            const historyRes = api.getSalaryConfigHistory ? await api.getSalaryConfigHistory(pvzFilter || '') : []
            configHistory = Array.isArray(historyRes) ? historyRes : (historyRes?.data || [])
          } catch (err) {
            configHistoryLoadFailed = true
            configHistory = []
          }

          // PHASE 3: STALE RESPONSE GUARD
          if (loadAllGenRef.current !== currentLoadAllGen) return

          // Для daily-сотрудников загружаем ONE BROAD READ BATCH на диапазон [batchStart, todayStr]
          const dailyEmployees = employees.filter(emp => {
            const freq = api.getEffectivePayoutFrequency
              ? api.getEffectivePayoutFrequency(emp, salaryConfig)
              : (emp.employment_type === 'substitute' ? 'weekly' : (salaryConfig?.payoutFrequency || 'bi_monthly'))
            return freq === 'daily'
          })

          let pvzScheduleForDaily = []
          let pvzWorkLogsForDaily = []
          let pvzPaymentsForDaily = []
          let pvzDeductionsForDaily = []
          let dailyDiscoveryFailure = false
          let dailyFinancialDataUnavailable = false
          let pvzEarliestFactualDate = null

          if (dailyEmployees.length > 0) {
            try {
              // Data Discovery Boundary для Daily:
              // Вычисляем персональные границы сотрудников.
              // СТРОГО НЕ включает earliestReliableSalaryHistory!
              const personalStarts = dailyEmployees.map(e => {
                const start = api.getDailyDataDiscoveryStart
                  ? api.getDailyDataDiscoveryStart(e, salaryConfig)
                  : (api.getDailyAuthoritativeStart ? api.getDailyAuthoritativeStart(e, salaryConfig) : (cutover || (e.hired_at ? e.hired_at.split('T')[0] : null)))
                return { employee: e, start }
              })
              const knownStarts = personalStarts.map(x => x.start).filter(Boolean)
              const hasEmployeesWithoutBoundary = personalStarts.some(x => !x.start)

              // Если есть хотя бы один сотрудник без персональной границы (hired_at=null, cutover=null):
              // Делаем ровно 1 сетевой запрос на весь ПВЗ (O(1) complexity)
              if (hasEmployeesWithoutBoundary) {
                try {
                  const res = await api.apiClient(`/schedule/earliest-activity?pvzId=${pvzFilter || ''}`)
                  if (res && Object.prototype.hasOwnProperty.call(res, 'earliestDate')) {
                    pvzEarliestFactualDate = res.earliestDate || null
                  } else {
                    dailyDiscoveryFailure = true
                  }
                } catch (err) {
                  dailyDiscoveryFailure = true
                }
              }

              // batchStart = MIN(knownStarts, pvzEarliestFactualDate, cutover)
              const starts = [...knownStarts]
              if (pvzEarliestFactualDate) starts.push(pvzEarliestFactualDate)
              if (cutover) starts.push(cutover)

              let batchStart = null
              if (starts.length > 0) {
                batchStart = starts.reduce((min, d) => (d < min ? d : min), starts[0])
              } else {
                batchStart = todayStr
              }

              if (batchStart > todayStr) {
                batchStart = todayStr
              }

              // ONE BROAD READ BATCH: 4 параллельных запроса на диапазон [batchStart, todayStr]
              const paramsBatch = new URLSearchParams({ pvzId: pvzFilter || '', startDate: batchStart, endDate: todayStr })
              const paramsPaymentsBatch = new URLSearchParams({ pvzId: pvzFilter || '', periodStart: batchStart, periodEnd: todayStr })

              const [
                schedRes,
                logsRes,
                pmtsRes,
                deductsRes
              ] = await Promise.all([
                api.apiClient('/schedule?' + paramsBatch.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
                api.apiClient('/work-logs?' + paramsBatch.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
                api.apiClient('/payments?' + paramsPaymentsBatch.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
                api.apiClient('/deductions?' + paramsBatch.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error }))
              ])

              // MISSING_FINANCIAL_DATA_MUST_NOT_BE_INTERPRETED_AS_EMPTY_FINANCIAL_DATA:
              // Если любой из 4 обязательных наборов упал с ошибкой, обычный расчёт долга запрещён!
              dailyFinancialDataUnavailable = Boolean(
                schedRes.error ||
                logsRes.error ||
                pmtsRes.error ||
                deductsRes.error
              )

              pvzScheduleForDaily = Array.isArray(schedRes.data) ? schedRes.data : (schedRes.data?.data || [])
              pvzWorkLogsForDaily = Array.isArray(logsRes.data) ? logsRes.data : (logsRes.data?.data || [])
              pvzPaymentsForDaily = Array.isArray(pmtsRes.data) ? pmtsRes.data : (pmtsRes.data?.data || [])
              pvzDeductionsForDaily = Array.isArray(deductsRes.data) ? deductsRes.data : (deductsRes.data?.data || [])
            } catch (batchErr) {
              console.warn('Ошибка загрузки broad batch для daily-сотрудников:', batchErr)
              dailyFinancialDataUnavailable = true
            }
          }

          // PHASE 3: STALE RESPONSE GUARD
          if (loadAllGenRef.current !== currentLoadAllGen) return

          const employeePeriodsMap = new Map()
          const uniqueRangesMap = new Map() // key: `${start}_${end}` => { start, end } (только non-daily)

          employees.forEach(emp => {
            const effectiveFreq = api.getEffectivePayoutFrequency
              ? api.getEffectivePayoutFrequency(emp, salaryConfig)
              : (emp.employment_type === 'substitute' ? 'weekly' : (salaryConfig?.payoutFrequency || 'bi_monthly'))

            if (effectiveFreq === 'shifts') {
              employeePeriodsMap.set(emp.id, { isShifts: true, isDaily: false, periods: [] })
              return
            }

            let targetPeriods = []

            if (effectiveFreq === 'daily') {
              const empEffectiveStart = api.getDailyDataDiscoveryStart
                ? api.getDailyDataDiscoveryStart(emp, salaryConfig)
                : (cutover || (emp.hired_at ? emp.hired_at.split('T')[0] : null))

              // Fail-Closed: если любой обязательный датасет недоступен
              if (dailyFinancialDataUnavailable) {
                employeePeriodsMap.set(emp.id, { isShifts: false, isDaily: true, financialDataUnavailable: true, discoveryUnavailable: false, periods: [] })
              } else if (!empEffectiveStart && dailyDiscoveryFailure) {
                // Fail-Closed: если у сотрудника нет персональной границы и сетевой запрос истории смен упал
                employeePeriodsMap.set(emp.id, { isShifts: false, isDaily: true, financialDataUnavailable: false, discoveryUnavailable: true, periods: [] })
              } else {
                if (api.discoverDailyCandidatePeriods) {
                  targetPeriods = api.discoverDailyCandidatePeriods(emp, pvzScheduleForDaily, salaryConfig, {
                    todayStr,
                    fallbackEarliestDate: pvzEarliestFactualDate,
                    getSalaryPeriods: api.getSalaryPeriods
                  })
                } else {
                  const empShiftDates = new Set()
                  const lowerBound = empEffectiveStart || pvzEarliestFactualDate
                  pvzScheduleForDaily.forEach(s => {
                    if (s.employee_id === emp.id) {
                      const dStr = typeof s.work_date === 'string' ? s.work_date.split('T')[0] : ''
                      if (dStr && (!lowerBound || dStr >= lowerBound) && dStr <= todayStr) {
                        let hours = parseFloat(s.hours) || 0
                        if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
                          hours = 0
                        }
                        if (hours > 0 || s.started_at) {
                          empShiftDates.add(dStr)
                        }
                      }
                    }
                  })
                  if (!lowerBound || todayStr >= lowerBound) {
                    empShiftDates.add(todayStr)
                  }

                  const uniqueMonths = new Set()
                  empShiftDates.forEach(dStr => {
                    uniqueMonths.add(dStr.substring(0, 7))
                  })

                  let dailyPeriods = []
                  uniqueMonths.forEach(ym => {
                    const [y, m] = ym.split('-').map(Number)
                    const monthPeriods = api.getSalaryPeriods
                      ? api.getSalaryPeriods(emp.hired_at, y, m, 'daily', emp.pay_day, [], cutover)
                      : []
                    dailyPeriods.push(...monthPeriods)
                  })

                  targetPeriods = dailyPeriods.filter(p => p.end <= todayStr && empShiftDates.has(p.start))
                  if (cutover) {
                    targetPeriods = targetPeriods.filter(p => p.end >= cutover)
                  }
                }

                employeePeriodsMap.set(emp.id, { isShifts: false, isDaily: true, financialDataUnavailable: false, discoveryUnavailable: false, periods: targetPeriods })
              }
              // DAILY ПЕРИОДЫ НЕ ДОБАВЛЯЮТСЯ В uniqueRangesMap!
            } else {
              // Для остальных периодичностей (weekly, bi_monthly, monthly):
              const prevMonthDate = new Date(nowYear, nowMonth - 2, 1)
              const prevYear = prevMonthDate.getFullYear()
              const prevMonth = prevMonthDate.getMonth() + 1

              if (emp.is_legacy) {
                // Для архивных/уволенных сотрудников:
                // Долг обязан оставаться видимым до полного погашения (ARCHIVED_UNPAID_DEBT_MUST_REMAIN_VISIBLE_UNTIL_SETTLED)
                // Независимо от смены календарных месяцев (Month Rollover).
                // Используем фактические activity_months (месяцы с реальной активностью на целевом ПВЗ: смены, логи, платежи, вычеты).
                // Не генерируем пустые промежуточные месяцы без активности (устраняем линейный scan O(N) -> O(M)).
                const legacyMonths = new Set(
                  Array.isArray(emp.activity_months) && emp.activity_months.length > 0
                    ? emp.activity_months
                    : [`${nowYear}-${String(nowMonth).padStart(2, '0')}`, `${prevYear}-${String(prevMonth).padStart(2, '0')}`]
                )

                // Если есть earliest_activity_date, но activity_months был пуст, гарантируем добавление его месяца
                if (emp.earliest_activity_date && emp.earliest_activity_date.includes('-')) {
                  legacyMonths.add(emp.earliest_activity_date.substring(0, 7))
                }

                const sortedMonths = Array.from(legacyMonths).sort()
                const candidatePeriods = []

                sortedMonths.forEach(ym => {
                  const [y, m] = ym.split('-').map(Number)
                  const mPeriods = api.getSalaryPeriods ? api.getSalaryPeriods(emp.hired_at, y, m, effectiveFreq, emp.pay_day, [], cutover) : []
                  mPeriods.forEach(p => {
                    if (p.start <= todayStr) {
                      if (!candidatePeriods.some(cp => cp.start === p.start && cp.end === p.end)) {
                        candidatePeriods.push(p)
                      }
                    }
                  })
                })

                if (cutover) {
                  targetPeriods = candidatePeriods.filter(p => p.end >= cutover)
                } else {
                  targetPeriods = candidatePeriods
                }
              } else {
                // Для действующих сотрудников стандартно: текущий период + предшествующий период
                const prevPeriods = api.getSalaryPeriods ? api.getSalaryPeriods(emp.hired_at, prevYear, prevMonth, effectiveFreq, emp.pay_day, [], cutover) : []
                const currPeriods = api.getSalaryPeriods ? api.getSalaryPeriods(emp.hired_at, nowYear, nowMonth, effectiveFreq, emp.pay_day, [], cutover) : []

                const allPossiblePeriods = [...prevPeriods, ...currPeriods]

                // 1) Период, в который попадает today (текущий)
                const currentPeriod = allPossiblePeriods.find(p => todayStr >= p.start && todayStr <= p.end)
                if (currentPeriod) {
                  targetPeriods.push(currentPeriod)
                }

                // 2) Непосредственно предшествующий период
                const previousPeriod = allPossiblePeriods
                  .filter(p => p.end < todayStr)
                  .sort((a, b) => b.end.localeCompare(a.end))[0]
                if (previousPeriod && !targetPeriods.some(tp => tp.start === previousPeriod.start && tp.end === previousPeriod.end)) {
                  targetPeriods.push(previousPeriod)
                }

                if (cutover) {
                  targetPeriods = targetPeriods.filter(p => p.end >= cutover)
                }
              }

              employeePeriodsMap.set(emp.id, { isShifts: false, isDaily: false, periods: targetPeriods })

              // Собираем уникальные интервалы ТОЛЬКО для non-daily диапазонов!
              targetPeriods.forEach(p => {
                const key = `${p.start}_${p.end}`
                if (!uniqueRangesMap.has(key)) {
                  uniqueRangesMap.set(key, { start: p.start, end: p.end })
                }
              })
            }
          })

          // Пакетно рассчитываем ЗП ТОЛЬКО по non-daily диапазонам (readOnly: true, DB write side effects = 0)
          const rangeResultsMap = new Map()
          await Promise.all(
            Array.from(uniqueRangesMap.values()).map(async range => {
              try {
                const list = await api.calculateTeamSalaries(
                  pvzFilter || undefined,
                  range.start,
                  range.end,
                  employees,
                  configHistory,
                  { mode: 'settlement', readOnly: true }
                )
                rangeResultsMap.set(`${range.start}_${range.end}`, { data: list || [], error: null })
              } catch (e) {
                console.error(`Ошибка calculateTeamSalaries для ${range.start}..${range.end}:`, e)
                rangeResultsMap.set(`${range.start}_${range.end}`, { data: null, error: e })
              }
            })
          )

          // PHASE 3: STALE RESPONSE GUARD
          if (loadAllGenRef.current !== currentLoadAllGen) return

          const extendedResults = []

          // Сопоставляем результаты для каждого сотрудника
          for (const emp of employees) {
            const empData = employeePeriodsMap.get(emp.id)
            if (!empData) continue

            if (empData.isShifts) {
              try {
                const shiftActual = await api.getEmployeeActualSalaryPeriod(emp.id, emp.hired_at, pvzFilter, emp, { mode: 'settlement', readOnly: true })
                if (loadAllGenRef.current !== currentLoadAllGen) return
                if (shiftActual?.financialDataUnavailable || shiftActual?.status === 'data_unavailable') {
                  extendedResults.push({
                    employeeId: emp.id,
                    employeeName: emp.name,
                    employment_type: emp.employment_type,
                    payout_frequency_override: emp.payout_frequency_override,
                    isLegacy: emp.is_legacy,
                    actualPeriod: shiftActual?.period || {
                      start: todayStr,
                      end: todayStr,
                      displayStart: todayStr,
                      label: 'Период недоступен',
                      effectiveFrequency: 'shifts'
                    },
                    actualDebt: 0,
                    upcomingDebt: 0,
                    unresolvedDebt: 0,
                    isPayable: false,
                    isReviewRequired: true,
                    financialDataUnavailable: true,
                    status: 'data_unavailable',
                    unresolvedLegacyNotice: shiftActual?.notice || 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — повторите загрузку.'
                  })
                } else if (shiftActual?.discoveryUnavailable) {
                  extendedResults.push({
                    employeeId: emp.id,
                    employeeName: emp.name,
                    employment_type: emp.employment_type,
                    payout_frequency_override: emp.payout_frequency_override,
                    isLegacy: emp.is_legacy,
                    actualPeriod: shiftActual.period,
                    actualDebt: 0,
                    upcomingDebt: 0,
                    unresolvedDebt: 0,
                    isPayable: false,
                    isReviewRequired: true,
                    discoveryUnavailable: true,
                    status: 'history_unavailable',
                    unresolvedLegacyNotice: shiftActual.notice || 'Не удалось проверить историю смен. Расчёт выплат временно недоступен — требуется повторная загрузка.'
                  })
                } else if (shiftActual && shiftActual.debt > 0 && shiftActual.isPayable !== false && shiftActual.isCompleteChunk !== false && shiftActual.period) {
                  extendedResults.push({
                    ...shiftActual.salary,
                    employeeId: emp.id,
                    employeeName: emp.name,
                    employment_type: emp.employment_type,
                    payout_frequency_override: emp.payout_frequency_override,
                    isLegacy: emp.is_legacy,
                    actualPeriod: shiftActual.period,
                    actualDebt: shiftActual.debt,
                    upcomingDebt: shiftActual.debt,
                    isCompleteChunk: shiftActual.isCompleteChunk,
                    hasOpenShift: shiftActual.hasOpenShift
                  })
                }
              } catch (e) {
                extendedResults.push({
                  employeeId: emp.id,
                  employeeName: emp.name,
                  employment_type: emp.employment_type,
                  payout_frequency_override: emp.payout_frequency_override,
                  isLegacy: emp.is_legacy,
                  actualPeriod: {
                    start: todayStr,
                    end: todayStr,
                    displayStart: todayStr,
                    label: 'Период недоступен',
                    effectiveFrequency: 'shifts'
                  },
                  actualDebt: 0,
                  upcomingDebt: 0,
                  unresolvedDebt: 0,
                  isPayable: false,
                  isReviewRequired: true,
                  financialDataUnavailable: true,
                  status: 'data_unavailable',
                  unresolvedLegacyNotice: 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — повторите загрузку.'
                })
              }
              continue
            }

            if (empData.isDaily) {
              if (empData.financialDataUnavailable) {
                extendedResults.push({
                  employeeId: emp.id,
                  employeeName: emp.name,
                  employment_type: emp.employment_type,
                  payout_frequency_override: emp.payout_frequency_override,
                  isLegacy: emp.is_legacy,
                  actualPeriod: {
                    start: todayStr,
                    end: todayStr,
                    displayStart: todayStr,
                    label: 'Период недоступен',
                    effectiveFrequency: 'daily'
                  },
                  actualDebt: 0,
                  upcomingDebt: 0,
                  unresolvedDebt: 0,
                  isPayable: false,
                  isReviewRequired: true,
                  financialDataUnavailable: true,
                  status: 'data_unavailable',
                  unresolvedLegacyNotice: 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — обновите данные и повторите попытку.'
                })
                continue
              }

              if (empData.discoveryUnavailable) {
                extendedResults.push({
                  employeeId: emp.id,
                  employeeName: emp.name,
                  employment_type: emp.employment_type,
                  payout_frequency_override: emp.payout_frequency_override,
                  isLegacy: emp.is_legacy,
                  actualPeriod: {
                    start: todayStr,
                    end: todayStr,
                    displayStart: todayStr,
                    label: 'Период недоступен',
                    effectiveFrequency: 'daily'
                  },
                  actualDebt: 0,
                  upcomingDebt: 0,
                  unresolvedDebt: 0,
                  isPayable: false,
                  isReviewRequired: true,
                  discoveryUnavailable: true,
                  status: 'history_unavailable',
                  unresolvedLegacyNotice: 'Не удалось проверить историю смен. Расчёт выплат временно недоступен — требуется повторная загрузка.'
                })
                continue
              }

              // Для daily: рассчитываем периоды ЧИСТО В ПАМЯТИ через канонический calculateSalaryFromData
              for (const p of empData.periods) {
                const dayLogs = pvzWorkLogsForDaily.filter(l => l.employee_id === emp.id && (l.work_date === p.start || l.date === p.start))
                const daySchedule = pvzScheduleForDaily.filter(s => s.employee_id === emp.id && s.work_date === p.start)
                const dayLinkedHelpers = pvzScheduleForDaily.filter(s => s.linked_to === emp.id && s.work_date === p.start)
                const dayAllSchedule = pvzScheduleForDaily.filter(s => s.work_date === p.start)
                const dayDeductions = pvzDeductionsForDaily.filter(d => d.employee_id === emp.id && d.date === p.start)
                const dayPayments = pvzPaymentsForDaily.filter(pm => {
                  if (pm.employee_id !== emp.id) return false
                  if (Array.isArray(pm.allocations) && pm.allocations.length > 0) {
                    return pm.allocations.some(a => a.period_start === p.start && a.period_end === p.end)
                  }
                  const pStart = pm.period_start || pm.date
                  const pEnd = pm.period_end || pm.date
                  if (cutover && p.start >= cutover && pEnd < cutover) return false
                  return (pEnd >= p.start && pStart <= p.end)
                })

                const s = api.calculateSalaryFromData(
                  {
                    id: emp.id,
                    role: emp.role,
                    hired_at: emp.hired_at,
                    pay_day: emp.pay_day,
                    employment_type: emp.employment_type,
                    payout_frequency_override: emp.payout_frequency_override
                  },
                  dayLogs,
                  daySchedule,
                  dayPayments,
                  dayDeductions,
                  dayLinkedHelpers,
                  salaryConfig,
                  p.start,
                  p.end,
                  configHistory,
                  dayAllSchedule,
                  todayStr,
                  { mode: 'settlement', readOnly: true, strictHistoricalCoverage: true }
                )

                const isHistoryUnreliable = s.payrollHistoryReliable === false
                const isHistoryLoadError = isHistoryUnreliable && configHistoryLoadFailed
                const isReview = Boolean(s.isReviewRequired || s.hasUnresolvedLegacy || isHistoryUnreliable)
                const hasOpenShift = Boolean(s.hasOpenShift || s.isOpenShift)
                const isPayable = s.isPayable !== false && !hasOpenShift && !isReview
                const debt = (isReview || !isPayable) ? 0 : Math.round(s.periodDebt ?? s.total)

                if (debt <= 0 && !isReview) continue

                extendedResults.push({
                  ...s,
                  isReviewRequired: isReview,
                  hasUnresolvedLegacy: isReview,
                  financialDataUnavailable: isHistoryLoadError,
                  status: isReview ? (isHistoryLoadError ? 'data_unavailable' : (isHistoryUnreliable ? 'history_unavailable' : 'unresolved')) : s.status,
                  unresolvedDebt: isReview ? (s.unresolvedDebt ?? Math.round(s.total)) : 0,
                  unresolvedLegacyNotice: isHistoryLoadError
                    ? 'Не удалось загрузить историю тарифов. Расчёт выплат временно недоступен — повторите загрузку.'
                    : (isHistoryUnreliable
                        ? 'ФОТ недоступен — нет истории тарифов на эту дату'
                        : s.unresolvedLegacyNotice),
                  employeeId: emp.id,
                  employeeName: emp.name,
                  employment_type: emp.employment_type,
                  payout_frequency_override: emp.payout_frequency_override,
                  isLegacy: emp.is_legacy,
                  actualPeriod: p,
                  actualDebt: debt,
                  upcomingDebt: debt,
                  hasOpenShift: hasOpenShift,
                  isOpenShift: hasOpenShift,
                  isPayable: isPayable
                })
              }
              continue
            }

            // Для остальных (non-daily, non-shifts):
            for (const p of empData.periods) {
              const key = `${p.start}_${p.end}`
              const rangeRes = rangeResultsMap.get(key)
              if (rangeRes && rangeRes.error) {
                extendedResults.push({
                  employeeId: emp.id,
                  employeeName: emp.name,
                  employment_type: emp.employment_type,
                  payout_frequency_override: emp.payout_frequency_override,
                  isLegacy: emp.is_legacy,
                  actualPeriod: p,
                  actualDebt: 0,
                  upcomingDebt: 0,
                  unresolvedDebt: 0,
                  isPayable: false,
                  isReviewRequired: true,
                  financialDataUnavailable: true,
                  status: 'data_unavailable',
                  unresolvedLegacyNotice: 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — повторите загрузку.'
                })
                continue
              }

              const teamSalariesForRange = (rangeRes && rangeRes.data) || []
              const s = teamSalariesForRange.find(item => item.employeeId === emp.id)
              if (!s) continue

              const isReview = Boolean(s.isReviewRequired || s.hasUnresolvedLegacy)
              const hasOpenShift = Boolean(s.hasOpenShift || s.isOpenShift)
              const isPayable = s.isPayable !== false && !hasOpenShift
              const debt = (isReview || !isPayable) ? 0 : Math.round(s.periodDebt ?? s.total)
              if (debt <= 0 && !isReview) continue

              extendedResults.push({
                ...s,
                isReviewRequired: isReview,
                hasUnresolvedLegacy: isReview,
                status: isReview ? 'unresolved' : s.status,
                unresolvedDebt: isReview ? (s.unresolvedDebt ?? Math.round(s.total)) : 0,
                employeeId: emp.id,
                employeeName: emp.name,
                employment_type: emp.employment_type,
                payout_frequency_override: emp.payout_frequency_override,
                isLegacy: emp.is_legacy,
                actualPeriod: p,
                actualDebt: debt,
                upcomingDebt: debt,
                hasOpenShift: hasOpenShift,
                isOpenShift: hasOpenShift,
                isPayable: isPayable
              })
            }
          }

          // PHASE 3: STALE RESPONSE GUARD
          if (loadAllGenRef.current !== currentLoadAllGen) return

          setExtendedSalaries(extendedResults)
        } catch (extErr) {
          console.error('Ошибка расчета расширенного периода ЗП:', extErr)
        }

        if (loadAllGenRef.current === currentLoadAllGen) {
          setLoadingAllSalaries(false)
        }
      } catch (err) {
        if (loadAllGenRef.current === currentLoadAllGen) {
          console.error('Ошибка в loadAll:', err)
          setLoadingAllSalaries(false)
        }
      }
    }
    loadAll()
  }, [
    isAdmin,
    employees,
    employeesLoadState,
    salaryConfigLoadState,
    salaryConfig,
    filterPeriodStart,
    filterPeriodEnd,
    filterEmployee,
    refreshTrigger,
    selectedPvzIds?.join(','),
    activePvz?.id
  ])

  const loadPayments = useCallback(async () => {
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    if (!profile?.id || !pvzFilter) return
    try {
      setLoading(true)
      const startFallback = filterPeriodStart || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
      const endFallback = filterPeriodEnd || new Date().toISOString().split('T')[0]
      const paymentData = await api.getPayments({
        type: (filterType === 'deduction') ? undefined : (filterType || undefined),
        employeeId: !isAdmin ? profile.id : (filterEmployee || undefined),
        periodStart: startFallback,
        periodEnd: endFallback,
        pvzId: pvzFilter || undefined,
      })
      const newPayments = filterType === 'deduction' ? [] : paymentData
      setPayments(newPayments)

      try {
        const pStart = new Date(startFallback).toISOString()
        const pEnd = new Date(endFallback + 'T23:59:59').toISOString()
        let deductionsData
        if (isAdmin) {
          deductionsData = await api.getDeductions(pStart, pEnd, pvzFilter)
          if (filterEmployee) {
            deductionsData = deductionsData.filter(d => d.employee_id === filterEmployee)
          }
        } else {
          deductionsData = await api.getMyDeductions(pStart, pEnd)
        }
        const newDeductions = (filterType && filterType !== 'deduction') ? [] : deductionsData
        setWbDeductions(newDeductions)
      } catch (e) {
        console.error('Ошибка загрузки удержаний:', e)
        setWbDeductions([])
      }
      try {
        const manualHoursData = await api.getManualHours(isAdmin, !isAdmin ? profile.id : null, startFallback, endFallback)
        setManualHours(manualHoursData)
      } catch (e) {
        console.error('Ошибка загрузки ручных часов:', e)
        setManualHours([])
      }
    } catch (err) {
      console.error('Ошибка загрузки:', err)
    } finally {
      setLoading(false)
    }
  }, [filterType, filterEmployee, filterPeriodStart, filterPeriodEnd, isAdmin, profile?.id, activePvz?.id, selectedPvzIds])

  useEffect(() => {
    loadPayments()
  }, [loadPayments, refreshTrigger])

  useEffect(() => {
    window.addEventListener('disputesUpdated', loadPayments)
    return () => window.removeEventListener('disputesUpdated', loadPayments)
  }, [loadPayments])

  // Авто-расчёт ЗП при выборе сотрудника (для админа)
  useEffect(() => {
    if (!filterEmployee) {
      if (isAdmin) setAutoSalary(null)
      return
    }
    const start = filterPeriodStart || today()
    const end = filterPeriodEnd || today()
    const emp = employees.find(e => e.id === filterEmployee)
    const cutover = salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null
    let isSettlement = false
    try {
      const startParts = start.split('-')
      const effectiveFreq = api.getEffectivePayoutFrequency ? api.getEffectivePayoutFrequency(emp, salaryConfig) : (salaryConfig?.payout_frequency || 'bi_monthly')
      const periods = api.getSalaryPeriods ? api.getSalaryPeriods(emp?.hired_at, parseInt(startParts[0], 10), parseInt(startParts[1], 10), effectiveFreq, emp?.pay_day, [], cutover) : []
      isSettlement = periods.some(p => p.start === start && p.end === end)
    } catch (e) {}
    api.calculateAutoSalary(filterEmployee, start, end, { mode: isSettlement ? 'settlement' : 'analytics', isAnalytics: !isSettlement })
      .then(res => {
        setAutoSalary({
          ...mapSalaryObject(res),
          employeeId: filterEmployee,
          employeeName: emp?.name || 'Неизвестно',
          actualPeriod: { start, end, displayStart: start }
        })
      })
      .catch(console.error)
  }, [filterEmployee, filterPeriodStart, filterPeriodEnd, refreshTrigger, employees, salaryConfig])

  const executeSubmit = async (overrideData = null) => {
    const data = overrideData || formData
    setSaving(true)
    try {
      const employee = employees.find((e) => e.id === data.employee_id)
      let fileUrl = data.saved_file_url || null
      if (data.file) {
        fileUrl = await api.uploadEvidenceFile(data.file)
      }

      let combinedUrl = null
      if (fileUrl && data.evidence_url_link) {
        combinedUrl = `${fileUrl}|${data.evidence_url_link}`
      } else {
        combinedUrl = fileUrl || data.evidence_url_link || null
      }

      if (editingId) {
        if (data.type.startsWith('deduction_')) {
          await api.updateDeduction(editingId, {
            employee_id: data.employee_id,
            type: data.type.replace('deduction_', ''),
            amount: parseFloat(data.amount),
            date: data.period_start,
            comment: data.comment,
            video_url: combinedUrl
          })
        } else {
          await api.updatePayment(editingId, {
            employee_id: data.employee_id,
            type: data.type,
            amount: parseFloat(data.amount),
            period_start: data.period_start,
            period_end: data.period_end,
            comment: data.comment,
            reason_code: data.type === 'fine' ? data.reason_code : null,
            evidence_url: combinedUrl
          })
        }
      } else {
        if (data.type.startsWith('deduction_')) {
          await api.addDeduction({
            employee_id: data.employee_id,
            type: data.type.replace('deduction_', ''),
            amount: parseFloat(data.amount),
            date: data.period_start,
            comment: data.comment,
            video_url: combinedUrl
          })
        } else {
          await api.createPayment({
            employee_id: data.employee_id,
            employee_name: employee?.name || 'Неизвестный',
            type: data.type,
            amount: parseFloat(data.amount),
            period_start: data.period_start,
            period_end: data.period_end,
            comment: data.comment,
            reason_code: data.type === 'fine' ? data.reason_code : null,
            evidence_url: combinedUrl,
            created_by: profile.id,
            created_by_name: profile.full_name,
          })
        }
      }

      resetForm()
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Ошибка сохранения:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.amount || !formData.employee_id) return
    const isNewBonusOrFine = !editingId && (formData.type === 'bonus' || formData.type === 'fine')

    if (isNewBonusOrFine) {
      setSaving(true)
      try {
        const checkResult = await api.checkPeriodPaid(formData.employee_id, formData.period_start, activePvz?.id)
        setSaving(false)

        if (checkResult.isPaid) {
          const employee = employees.find(e => e.id === formData.employee_id)
          setPaidPeriodModal({
            periodLabel: checkResult.periodLabel,
            periodStart: checkResult.periodStart,
            periodEnd: checkResult.periodEnd,
            amount: parseFloat(formData.amount),
            type: formData.type,
            employeeName: employee?.name || 'Сотрудник',
            nextPeriodStart: checkResult.nextPeriodStart,
            paid: checkResult.paid,
            earned: checkResult.earned,
            pendingFormData: { ...formData },
          })
          return
        }
      } catch (err) {
        console.error('Ошибка проверки периода:', err)
        setSaving(false)
      }
    }
    await executeSubmit()
  }

  const handlePaidPeriodPayNow = async () => {
    if (!paidPeriodModal) return
    setPaidPeriodSaving(true)
    try {
      await executeSubmit(paidPeriodModal.pendingFormData)
      if (paidPeriodModal.type === 'bonus') {
        const employee = employees.find(e => e.id === paidPeriodModal.pendingFormData.employee_id)
        await api.createPayment({
          employee_id: paidPeriodModal.pendingFormData.employee_id,
          employee_name: employee?.name || 'Неизвестный',
          type: 'salary',
          amount: paidPeriodModal.amount,
          period_start: paidPeriodModal.periodStart || paidPeriodModal.pendingFormData.period_start,
          period_end: paidPeriodModal.periodEnd || paidPeriodModal.pendingFormData.period_end,
          comment: `Доплата: ${paidPeriodModal.pendingFormData.comment || 'Премия'} за ${paidPeriodModal.periodLabel}`,
          created_by: profile.id,
          created_by_name: profile.full_name,
        })
        toast.success(`Премия сохранена и доплата ${formatMoney(paidPeriodModal.amount)} проведена`)
      } else {
        toast.success('Штраф сохранён. Сумма будет вычтена из следующей выплаты.')
      }
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Ошибка доплаты:', err)
      toast.error('Ошибка при проведении доплаты')
    } finally {
      setPaidPeriodSaving(false)
      setPaidPeriodModal(null)
    }
  }

  const handlePaidPeriodTransfer = async () => {
    if (!paidPeriodModal) return
    setPaidPeriodSaving(true)
    try {
      const modifiedData = {
        ...paidPeriodModal.pendingFormData,
        period_start: paidPeriodModal.nextPeriodStart,
        period_end: paidPeriodModal.nextPeriodStart,
        comment: (paidPeriodModal.pendingFormData.comment || (paidPeriodModal.type === 'bonus' ? 'Премия' : 'Штраф')) + ` (перенос из ${paidPeriodModal.periodLabel})`,
      }
      await executeSubmit(modifiedData)
      toast.success(`${paidPeriodModal.type === 'bonus' ? 'Премия' : 'Штраф'} перенесён в следующий период`)
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Ошибка переноса:', err)
      toast.error('Ошибка при переносе')
    } finally {
      setPaidPeriodSaving(false)
      setPaidPeriodModal(null)
    }
  }

  const resetForm = () => {
    setFormData({
      employee_id: '',
      type: 'bonus',
      amount: '',
      period_start: today(),
      period_end: today(),
      comment: '',
      reason_code: '',
      file: null,
      preview_url: '',
      saved_file_url: '',
      evidence_url_link: '',
    })
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (payment) => {
    const isDeduction = payment._source === 'deduction'
    const rawUrl = isDeduction ? payment.video_url : payment.evidence_url
    let savedFileUrl = ''
    let evidenceUrlLink = ''

    if (rawUrl) {
      if (rawUrl.includes('|')) {
        const parts = rawUrl.split('|')
        savedFileUrl = parts[0]
        evidenceUrlLink = parts[1]
      } else {
        if (isImageUrl(rawUrl)) {
          savedFileUrl = rawUrl
        } else {
          evidenceUrlLink = rawUrl
        }
      }
    }

    setFormData({
      employee_id: payment.employee_id,
      type: isDeduction ? 'deduction_' + payment.type : payment.type,
      amount: payment.amount.toString(),
      period_start: isDeduction ? payment.date : payment.period_start,
      period_end: isDeduction ? payment.date : payment.period_end,
      comment: payment.comment || '',
      reason_code: payment.reason_code || '',
      file: null,
      preview_url: '',
      saved_file_url: savedFileUrl,
      evidence_url_link: evidenceUrlLink,
    })
    setEditingId(payment.id)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const confirmDelete = async () => {
    if (!itemToDelete) return
    try {
      if (itemToDelete.source === 'deduction') {
        await api.deleteDeduction(itemToDelete.id)
      } else {
        await api.deletePayment(itemToDelete.id)
      }
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Ошибка удаления:', err)
    } finally {
      setItemToDelete(null)
    }
  }

  const handleDeletePayout = async (paymentId, amount, empName) => {
    if (!window.confirm(`Вы действительно хотите отменить выплату сотруднику ${empName} на сумму ${formatMoney(amount)}?\nЭта операция вернет сумму обратно в долг.`)) return
    try {
      await api.deletePayment(paymentId)
      toast.success('Выплата успешно отменена')
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      toast.error('Не удалось отменить выплату: ' + err.message)
    }
  }

  const [isClearModalOpen, setIsClearModalOpen] = useState(false)
  const confirmClearRejected = async () => {
    try {
      await api.clearRejectedPayments()
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Ошибка очистки:', err)
    } finally {
      setIsClearModalOpen(false)
    }
  }

  const handleResolveManualHour = async (empId, date, status) => {
    try {
      await api.resolveManualHours(empId, date, status)
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
      toast.success(status === 'approved' ? 'Часы подтверждены' : 'Часы отклонены')
    } catch (err) {
      console.error('Ошибка обработки ручных часов:', err)
      toast.error('Ошибка')
    }
  }

  const submitDispute = async (e) => {
    e.preventDefault()
    if (!disputeComment.trim()) return
    setDisputing(true)
    try {
      let evidenceUrl = null
      if (disputeFile) {
        evidenceUrl = await api.uploadEvidenceFile(disputeFile)
      }
      const videoLink = disputeVideoLink.trim() || null

      if (disputeId?.source === 'deduction') {
        await api.disputeDeduction(disputeId.id, disputeComment.trim(), evidenceUrl, videoLink)
      } else {
        await api.disputePayment(disputeId?.id || disputeId, disputeComment.trim(), evidenceUrl, videoLink)
      }
      setDisputeId(null)
      setDisputeComment('')
      setDisputeFile(null)
      setDisputePreview(null)
      setDisputeVideoLink('')
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
      window.dispatchEvent(new Event('disputesUpdated'))
    } catch (err) {
      console.error('Ошибка оспаривания:', err)
    } finally {
      setDisputing(false)
    }
  }

  const [disputeToResolve, setDisputeToResolve] = useState(null)

  const confirmResolveDispute = async () => {
    if (!disputeToResolve) return
    try {
      if (disputeToResolve.source === 'deduction') {
        await api.resolveDeductionDispute(disputeToResolve.id, disputeToResolve.isApproved)
      } else {
        await api.resolveDispute(disputeToResolve.id, disputeToResolve.isApproved)
      }
      await loadPayments()
      setRefreshTrigger(prev => prev + 1)
      window.dispatchEvent(new Event('disputesUpdated'))
    } catch (err) {
      console.error('Ошибка разрешения спора:', err)
    } finally {
      setDisputeToResolve(null)
    }
  }

  const totalBonus = payments
    .filter((p) => p.type === 'bonus' && p.status !== 'disputed' && p.status !== 'rejected')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const totalFines = payments
    .filter((p) => p.type === 'fine' && p.status !== 'disputed' && p.status !== 'rejected')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)

  const totalWbDed = wbDeductions
    .filter(d => d.status !== 'rejected')
    .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0)

  const hasActiveFilters = filterType || filterEmployee || filterPeriodStart || filterPeriodEnd

  const salaryPayments = useMemo(() => {
    return payments.filter(p => p.type === 'salary' && p.status !== 'cancelled' && p.status !== 'rejected')
  }, [payments])

  const paidByEmployee = useMemo(() => {
    const map = {}
    salaryPayments.forEach(p => {
      if (!map[p.employee_id]) map[p.employee_id] = 0
      map[p.employee_id] += Number(p.amount) || 0
    })
    return map
  }, [salaryPayments])

  const hideArchive = salaryConfig?.hideArchiveInSalary !== false

  const grandTotal = allSalaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)
  const grandPaid = Object.values(paidByEmployee).reduce((sum, v) => sum + v, 0)

  const avgSalaryData = useMemo(() => {
    const mainSalaries = allSalaries.filter(s => !s.isHelper)
    let totalPay = 0
    let shiftCount = 0

    mainSalaries.forEach(s => {
      if (s.dailyDetails) {
        Object.entries(s.dailyDetails).forEach(([date, dayData]) => {
          const hours = Number(dayData?.hours) || 0
          if (hours < 12) return
          const hasBonusOperations =
            (salaryConfig?.bonusPerAccepted > 0 && (Number(dayData?.acceptBonus) > 0)) ||
            (salaryConfig?.bonusPerIssued > 0 && (Number(dayData?.issuedBonus) > 0)) ||
            (salaryConfig?.bonusPerReturned > 0 && (Number(dayData?.returnBonus) > 0)) ||
            (Number(dayData?.customWorkBonus) > 0)

          if (hasBonusOperations) {
            const dayEarned = (Number(dayData?.basePay) || 0) +
                              (Number(dayData?.acceptBonus) || 0) +
                              (Number(dayData?.issuedBonus) || 0) +
                              (Number(dayData?.returnBonus) || 0) +
                              (Number(dayData?.customWorkBonus) || 0)
            totalPay += dayEarned
            shiftCount++
          }
        })
      }
    })
    const avg = shiftCount > 0 ? (totalPay / shiftCount) : 0
    return { total: totalPay, days: shiftCount, avg }
  }, [allSalaries, salaryConfig])

  const showAcceptance = salaryConfig?.bonusPerAccepted > 0
  const showIssued = salaryConfig?.bonusPerIssued > 0
  const showReturns = salaryConfig?.bonusPerReturned > 0

  const customColumns = useMemo(() => {
    const cols = new Set()
    allSalaries.forEach(s => {
      if (s.customWorkDetails) {
        s.customWorkDetails.forEach(d => cols.add(d.name))
      }
    })
    return Array.from(cols).sort()
  }, [allSalaries])

  const colDefs = [
    'minmax(180px, 2.5fr)',
    'minmax(35px, 0.6fr)',
    'minmax(55px, 0.9fr)',
  ]
  if (showAcceptance) colDefs.push('minmax(65px, 1fr)')
  if (showIssued) colDefs.push('minmax(65px, 1fr)')
  if (showReturns) colDefs.push('minmax(65px, 1fr)')
  customColumns.forEach(() => colDefs.push('minmax(80px, 1fr)'))
  colDefs.push('minmax(75px, 1.1fr)')
  colDefs.push('minmax(75px, 1.1fr)')
  colDefs.push('minmax(110px, 1.4fr)')
  const gridCols = colDefs.join(' ')

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'
  const renderAvatar = (url, name, clickable = false) => {
    if (!url) return getInitials(name)
    return (
      <img
        src={url}
        alt={name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onClick={(e) => { if (clickable && url) { e.stopPropagation(); setImagePreviewModal(url); } }}
      />
    )
  }

  return (
    <div className={`payments-page animate-fade-in ${isEmbedded ? 'embedded' : ''}`}>
      {/* Header */}
      {!isEmbedded && (
        <div className="payments-header" data-tour="payments-header">
          <div className="payments-header-left">
            <span className="payments-header-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span>
            <div>
              <h1 className="payments-title">Зарплаты</h1>
              <p className="payments-subtitle">
                {isAdmin ? 'Управление расчётами, премиями и удержаниями' : 'Мои выплаты и удержания'}
              </p>
            </div>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setExportSelectedEmployees(employees.filter(e => !e.is_legacy).map(e => e.id))
                  setShowExportModal(true)
                }}
                title="Экспорт ведомости в Excel/CSV"
              >
                Экспорт
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setIsClearModalOpen(true)}
                title="Удалить все отменённые записи"
              >
                Очистить отменённые
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetForm()
                  setShowForm(true)
                }}
              >
                + Добавить выплату/штраф
              </button>
            </div>
          )}
        </div>
      )}

      {isEmbedded && isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setExportSelectedEmployees(employees.filter(e => !e.is_legacy).map(e => e.id))
              setShowExportModal(true)
            }}
          >
            Экспорт
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить запись
          </button>
        </div>
      )}

      {/* Stats */}
      <div data-tour="payments-table">
      <PaymentStats
        loadingAllSalaries={loadingAllSalaries}
        loading={loading}
        totalBonus={totalBonus}
        totalFines={totalFines}
        totalWbDed={totalWbDed}
        grandTotal={grandTotal}
        grandPaid={grandPaid}
        grandRemaining={allSalaries.some(s => s.isAnalyticsRange)
          ? null
          : (extendedSalaries.length > 0
            ? extendedSalaries.reduce((sum, s) => sum + Math.max(s.actualDebt || 0, 0), 0)
            : Math.max(grandTotal - grandPaid, 0))}
        avgSalaryData={avgSalaryData}
        payments={payments}
        wbDeductions={wbDeductions}
        employees={employees}
        profile={profile}
        isAdmin={isAdmin}
        allSalaries={allSalaries}
        filterEmployee={filterEmployee}
        formatMoney={formatMoney}
        setStatDetailModal={setStatDetailModal}
      />
      </div>

      {/* Admin Form Modal */}
      <PaymentFormModal
        showForm={showForm}
        resetForm={resetForm}
        handleSubmit={handleSubmit}
        saving={saving}
        editingId={editingId}
        isAdmin={isAdmin}
        formData={formData}
        setFormData={setFormData}
        employees={employees.filter(emp => {
          if (!emp.is_legacy) return true;
          const targetList = extendedSalaries.length > 0 ? extendedSalaries : allSalaries;
          return targetList.some(s => s.employeeId === emp.id && s.actualDebt > 0);
        })}
      />

      {/* Paid Period Check Modal */}
      <PaidPeriodModal
        paidPeriodModal={paidPeriodModal}
        setPaidPeriodModal={setPaidPeriodModal}
        paidPeriodSaving={paidPeriodSaving}
        handlePaidPeriodPayNow={handlePaidPeriodPayNow}
        handlePaidPeriodTransfer={handlePaidPeriodTransfer}
        formatMoney={formatMoney}
      />

      {/* Dispute Modal */}
      <DisputeModal
        disputeId={disputeId}
        setDisputeId={setDisputeId}
        disputeComment={disputeComment}
        setDisputeComment={setDisputeComment}
        disputing={disputing}
        submitDispute={submitDispute}
        disputeFile={disputeFile}
        setDisputeFile={setDisputeFile}
        disputePreview={disputePreview}
        setDisputePreview={setDisputePreview}
        disputeVideoLink={disputeVideoLink}
        setDisputeVideoLink={setDisputeVideoLink}
      />

      {/* Filters */}
      <PaymentsFilters
        filterType={filterType}
        setFilterType={setFilterType}
        filterEmployee={filterEmployee}
        setFilterEmployee={setFilterEmployee}
        filterPeriodStart={filterPeriodStart}
        setFilterPeriodStart={setFilterPeriodStart}
        filterPeriodEnd={filterPeriodEnd}
        setFilterPeriodEnd={setFilterPeriodEnd}
        isAdmin={isAdmin}
        hideArchive={hideArchive}
        employees={employees}
        hasActiveFilters={hasActiveFilters}
        payoutFrequency={salaryConfig?.payoutFrequency || salaryConfig?.payout_frequency || 'bi_monthly'}
        settlementCutoverDate={salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null}
      />

      {/* All Salaries Summary (Admin) */}
      {isAdmin && !filterEmployee && (allSalaries.length > 0 || loadingAllSalaries) && (
        <div data-tour="payments-salary-table">
        <AllSalariesSummary
          allSalaries={allSalaries}
          extendedSalaries={extendedSalaries}
          loadingAllSalaries={loadingAllSalaries}
          employees={employees}
          profile={profile}
          isAdmin={isAdmin}
          isOwner={isOwner}
          canManagePayments={canManagePayments}
          expandedEmployeeId={expandedEmployeeId}
          setExpandedEmployeeId={setExpandedEmployeeId}
          filterPeriodStart={filterPeriodStart}
          filterPeriodEnd={filterPeriodEnd}
          filterEmployee={filterEmployee}
          showAcceptance={showAcceptance}
          showIssued={showIssued}
          showReturns={showReturns}
          customColumns={customColumns}
          gridCols={gridCols}
          formatMoney={formatMoney}
          formatDate={formatDate}
          handleOpenPayModal={handleOpenPayModal}
          showUpcomingPayouts={showUpcomingPayouts}
          setShowUpcomingPayouts={setShowUpcomingPayouts}
          avgSalaryData={avgSalaryData}
          toast={toast}
          setRefreshTrigger={setRefreshTrigger}
          setShowPayoutsDetailModal={setShowPayoutsDetailModal}
          setSalaryDetailModal={setSalaryDetailModal}
          totalPaid={grandPaid}
        />
        </div>
      )}

      {/* Employee / Filtered view */}
      {(!isAdmin || filterEmployee) && (
        <>
          {autoSalary ? (
            <div className="card animate-fade-in" style={{ padding: '24px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                <span style={{ fontWeight: 700, fontSize: '15px' }}>Детализация расчёта зарплаты:</span>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Период: <strong>{formatDate(filterPeriodStart)} — {formatDate(filterPeriodEnd)}</strong>
                </span>
              </div>
              {autoSalary && autoSalary.daysWorked > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, var(--wb-primary-ultra-light), rgba(99, 102, 241, 0.05))',
                  border: '1px dashed var(--wb-primary-glow)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Мой средний заработок за смену:</span>
                  <span style={{ fontWeight: 700, color: 'var(--wb-primary)', fontSize: '16px' }}>
                    {formatMoney(Math.round(autoSalary.total / autoSalary.daysWorked))}
                  </span>
                </div>
              )}
              <AutoSalaryBreakdown
                autoSalary={autoSalary}
                filterEmployee={filterEmployee || profile?.id}
                profile={profile}
                filterPeriodStart={filterPeriodStart}
                filterPeriodEnd={filterPeriodEnd}
                setSalaryDetailModal={setSalaryDetailModal}
                isAdmin={isAdmin}
                isOwner={isOwner}
                onPaySalary={() => handleOpenPayModal(autoSalary)}
              />
            </div>
          ) : (
            <div className="card animate-fade-in" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Загрузка расчёта...
            </div>
          )}
        </>
      )}

      {/* Payments and Deductions List */}
      <PaymentsList
        isAdmin={isAdmin}
        loading={loading}
        payments={payments}
        wbDeductions={wbDeductions}
        manualHours={manualHours}
        filterEmployee={filterEmployee}
        employees={employees}
        profile={profile}
        autoSalary={autoSalary}
        today={today}
        firstDayOfMonth={firstDayOfMonth}
        lastDayOfMonth={lastDayOfMonth}
        formatMoney={formatMoney}
        formatDate={formatDate}
        formatDateTime={formatDateTime}
        renderEvidence={renderEvidence}
        typeConfig={typeConfig}
        setCommentModal={setCommentModal}
        setImagePreviewModal={setImagePreviewModal}
        setDisputeId={setDisputeId}
        setDisputeToResolve={setDisputeToResolve}
        handleEdit={handleEdit}
        setItemToDelete={setItemToDelete}
        filterPeriodStart={filterPeriodStart}
        filterPeriodEnd={filterPeriodEnd}
        handleResolveManualHour={handleResolveManualHour}
      />

      {/* Pay Salary Modal */}
      <PaySalaryModal
        payModalData={payModalData}
        isFromAnalytics={Boolean(payModalData?.isFromAnalytics)}
        setPayModalData={setPayModalData}
        customPeriodStart={customPeriodStart}
        setCustomPeriodStart={setCustomPeriodStart}
        customPeriodEnd={customPeriodEnd}
        setCustomPeriodEnd={setCustomPeriodEnd}
        employeeShifts={employeeShifts}
        isCalculating={isCalculating}
        includeRate={includeRate}
        setIncludeRate={setIncludeRate}
        includeBonuses={includeBonuses}
        setIncludeBonuses={setIncludeBonuses}
        includeAdjustments={includeAdjustments}
        setIncludeAdjustments={setIncludeAdjustments}
        isManualAmount={isManualAmount}
        setIsManualAmount={setIsManualAmount}
        calculatedData={calculatedData}
        payAmount={payAmount}
        setPayAmount={setPayAmount}
        payComment={payComment}
        setPayComment={setPayComment}
        setIsCommentManuallyEdited={setIsCommentManuallyEdited}
        showPayModalDetails={showPayModalDetails}
        setShowPayModalDetails={setShowPayModalDetails}
        profile={profile}
        isAdmin={isAdmin}
        filterPeriodStart={filterPeriodStart}
        filterPeriodEnd={filterPeriodEnd}
        setSalaryDetailModal={setSalaryDetailModal}
        payingSalary={payingSalary}
        setPayingSalary={setPayingSalary}
        setRefreshTrigger={setRefreshTrigger}
        toast={toast}
      />

      {/* Comment Modal */}
      <Modal
        isOpen={!!commentModal}
        onClose={() => setCommentModal(null)}
        title={commentModal?.title || 'Комментарий'}
        isBottomSheet={true}
      >
        <div style={{ padding: '8px 0 24px 0', lineHeight: 1.7, whiteSpace: 'pre-wrap', color: 'var(--text-primary)', fontSize: '15px' }}>
          {commentModal?.text || 'Комментарий отсутствует'}
        </div>
      </Modal>

      {/* Salary Detail breakdown modal */}
      <SalaryDetailModal
        salaryDetailModal={salaryDetailModal}
        setSalaryDetailModal={setSalaryDetailModal}
        isAdmin={isAdmin}
        profile={profile}
        handleEdit={handleEdit}
        setItemToDelete={setItemToDelete}
        setDisputeId={setDisputeId}
      />

      {/* Stat Detail modal */}
      <Modal
        isOpen={!!statDetailModal}
        onClose={() => setStatDetailModal(null)}
        title={statDetailModal?.title || 'Детализация'}
        isBottomSheet={true}
      >
        {statDetailModal && (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {statDetailModal.items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-secondary)' }}>
                <div>Нет записей за выбранный период</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {statDetailModal.items.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 14px', background: 'var(--surface-hover)', borderRadius: 12,
                      borderLeft: `3px solid ${statDetailModal.color}`,
                      transition: 'transform 0.15s ease',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>{item.name}</span>
                          {item.badge && (
                            <span style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 6,
                              background: item.badge.includes('WB') ? 'rgba(239,68,68,0.1)' : item.badge.includes('Индив') ? 'rgba(203,17,171,0.1)' : 'rgba(245,158,11,0.1)',
                              color: item.badge.includes('WB') ? '#ef4444' : item.badge.includes('Индив') ? 'var(--wb-primary)' : '#f59e0b',
                              fontWeight: 600,
                            }}>{item.badge}</span>
                          )}
                        </div>
                        {item.comment && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{item.comment}</div>}
                        {item.date && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{formatDate(item.date)}</div>}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          fontWeight: 700, fontSize: 15,
                          color: statDetailModal.color,
                          whiteSpace: 'nowrap',
                        }}>
                          {formatMoney(item.amount)}
                        </div>

                        {/* Управление для владельца/администратора */}
                        {isAdmin && item.rawRecord && (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {item.rawRecord.status === 'disputed' ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  className="btn btn-success btn-xs"
                                  style={{ fontSize: '11px', padding: '4px 8px', minHeight: 'auto', height: 'auto', borderRadius: '6px', background: 'var(--success)', color: 'white', border: 'none', cursor: 'pointer' }}
                                  onClick={() => {
                                    setStatDetailModal(null);
                                    setDisputeToResolve({ id: item.rawRecord.id, source: item.rawRecord._source, isApproved: false });
                                  }}
                                  title="Отменить штраф/удержание"
                                >
                                  Отменить
                                </button>
                                <button
                                  className="btn btn-warning btn-xs"
                                  style={{ fontSize: '11px', padding: '4px 8px', minHeight: 'auto', height: 'auto', borderRadius: '6px', background: 'var(--warning)', color: 'white', border: 'none', cursor: 'pointer' }}
                                  onClick={() => {
                                    setStatDetailModal(null);
                                    setDisputeToResolve({ id: item.rawRecord.id, source: item.rawRecord._source, isApproved: true });
                                  }}
                                  title="Штраф в силе"
                                >
                                  Оставить
                                </button>
                              </div>
                            ) : (
                              <>
                                {item.rawRecord.type !== 'salary' && (
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    style={{ padding: '6px', minHeight: 'auto', height: 'auto', display: 'flex', borderRadius: '8px', cursor: 'pointer' }}
                                    onClick={() => {
                                      setStatDetailModal(null);
                                      handleEdit(item.rawRecord);
                                    }}
                                    title="Редактировать"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                )}
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ padding: '6px', minHeight: 'auto', height: 'auto', color: 'var(--danger)', display: 'flex', borderRadius: '8px', cursor: 'pointer' }}
                                  onClick={() => {
                                    setStatDetailModal(null);
                                    setItemToDelete({ id: item.rawRecord.id, source: item.rawRecord._source });
                                  }}
                                  title="Удалить"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* Оспаривание для сотрудников */}
                        {item.rawRecord && item.rawRecord.employee_id === profile?.id && (item.rawRecord.type === 'fine' || item.rawRecord._source === 'deduction') && item.rawRecord.status !== 'disputed' && item.rawRecord.status !== 'rejected' && (
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ fontSize: '11px', padding: '4px 8px', color: 'orange', borderColor: 'orange', minHeight: 'auto', height: 'auto', borderRadius: '6px', cursor: 'pointer' }}
                            onClick={() => {
                              setStatDetailModal(null);
                              setDisputeId({ id: item.rawRecord.id, source: item.rawRecord._source });
                            }}
                          >
                            Оспорить
                          </button>
                        )}

                        {/* Статус оспаривания */}
                        {item.rawRecord && item.rawRecord.status === 'disputed' && (
                          <span style={{ fontSize: '11px', color: 'orange', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> Оспаривается
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 16px', marginTop: 12,
                  background: `linear-gradient(135deg, ${statDetailModal.color}15, ${statDetailModal.color}08)`,
                  borderRadius: 12, fontWeight: 700, fontSize: 16,
                  borderTop: `2px solid ${statDetailModal.color}30`,
                }}>
                  <span>Итого:</span>
                  <span style={{ color: statDetailModal.color }}>
                    {formatMoney(statDetailModal.items.reduce((s, item) => s + (statDetailModal.title.includes('Начислено за период') ? Math.max(item.amount || 0, 0) : (item.amount || 0)), 0))}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Удаление записи" isBottomSheet={true} onConfirm={confirmDelete}>
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите удалить эту запись? Это действие нельзя отменить.
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setItemToDelete(null)}>
            Отмена
          </button>
          <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)' }} onClick={confirmDelete}>
            Удалить
          </button>
        </div>
      </Modal>

      {/* Clear canceled confirmation modal */}
      <Modal isOpen={isClearModalOpen} onClose={() => setIsClearModalOpen(false)} title="Очистка отменённых" isBottomSheet={true} onConfirm={confirmClearRejected}>
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите удалить все отменённые записи? Это действие нельзя отменить, и они навсегда пропадут из истории.
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setIsClearModalOpen(false)}>
            Отмена
          </button>
          <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)' }} onClick={confirmClearRejected}>
            Очистить
          </button>
        </div>
      </Modal>

      {/* Resolve dispute confirmation modal */}
      <Modal isOpen={!!disputeToResolve} onClose={() => setDisputeToResolve(null)} title="Разрешение спора" isBottomSheet={true} onConfirm={confirmResolveDispute}>
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          {disputeToResolve?.isApproved ? 'Вы уверены, что хотите оставить штраф в силе?' : 'Вы уверены, что хотите отменить штраф? Сотруднику будут возвращены средства.'}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setDisputeToResolve(null)}>
            Назад
          </button>
          <button className="btn btn-primary" style={{ flex: 1, background: disputeToResolve?.isApproved ? 'var(--warning)' : 'var(--danger)' }} onClick={confirmResolveDispute}>
            Подтвердить
          </button>
        </div>
      </Modal>

      {/* Export modal */}
      <ExportModal
        showExportModal={showExportModal}
        setShowExportModal={setShowExportModal}
        exportStartDate={exportStartDate}
        setExportStartDate={setExportStartDate}
        exportEndDate={exportEndDate}
        setExportEndDate={setExportEndDate}
        exportSelectedEmployees={exportSelectedEmployees}
        setExportSelectedEmployees={setExportSelectedEmployees}
        employees={employees}
        exporting={exporting}
        handleExportSalaries={handleExportSalaries}
      />

      {/* Payouts detail list modal */}
      <PayoutsDetailModal
        showPayoutsDetailModal={showPayoutsDetailModal}
        setShowPayoutsDetailModal={setShowPayoutsDetailModal}
        filterPeriodStart={filterPeriodStart}
        filterPeriodEnd={filterPeriodEnd}
        loadingPayoutsDetail={loading}
        payoutsDetailList={salaryPayments}
        employees={employees}
        isAdmin={isAdmin}
        handleDeletePayout={handleDeletePayout}
      />

      {/* Lightbox Image Preview Modal */}
      {imagePreviewModal && (
        <Modal
          isOpen={!!imagePreviewModal}
          onClose={() => setImagePreviewModal(null)}
          isLightbox={true}
        >
          {(() => {
            const cleanUrl = imagePreviewModal.split('?')[0].toLowerCase()
            const isVideo = cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.mov') || cleanUrl.endsWith('.avi') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.mkv') || cleanUrl.includes('/video')
            if (isVideo) {
              return (
                <video
                  src={imagePreviewModal}
                  controls
                  autoPlay
                  style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', background: '#000' }}
                />
              )
            } else {
              return (
                <img
                  src={imagePreviewModal}
                  alt="Увеличенное фото"
                  style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
                />
              )
            }
          })()}
        </Modal>
      )}

    </div>
  )
}
